import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

// Add consistent styling matching homepage-demo.html
const addConsistentStyles = () => {
  if (document.getElementById('login-modal-styles')) return;

  const style = document.createElement('style');
  style.id = 'login-modal-styles';
  style.textContent = `
    .font-playfair { font-family: 'Playfair Display', serif; }
    .font-dm-sans { font-family: 'DM Sans', sans-serif; }
    .text-primary { color: #1CB7C8; }
    .bg-primary { background: linear-gradient(135deg, #1CB7C8 0%, #159BA9 100%); }
    .bg-primary-light { background-color: rgba(28, 183, 200, 0.1); }
    .text-charcoal { color: #1A1A1A; }
    .border-primary { border-color: #1CB7C8; }
    .hover\\:bg-primary-dark:hover { background: linear-gradient(135deg, #159BA9 0%, #127A85 100%); }
    .hover\\:text-primary:hover { color: #1CB7C8; }
    .focus\\:ring-primary:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(28, 183, 200, 0.2);
      border-color: #1CB7C8;
    }
  `;
  document.head.appendChild(style);
};

const LoginPage = ({ onClose, onSwitchToSignup, onSwitchToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn } = useAuth();

  // Add consistent styles and fonts
  useEffect(() => {
    addConsistentStyles();

    // Add Google Fonts (Playfair Display & DM Sans)
    if (!document.getElementById('login-modal-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'login-modal-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 handleSubmit called', { email, password, signInType: typeof signIn });
    setLoading(true);
    setError('');

    try {
      console.log('🔍 About to call signIn...');
      const result = await signIn(email, password);
      console.log('🔍 signIn result:', result);
      if (result.success) {
        onClose && onClose();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('🔍 signIn catch error:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackgroundClick}
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 relative font-dm-sans" style={{ border: '1px solid rgba(209, 213, 219, 0.3)' }}>
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-6">
            <img src="/maths_logo_teal.png" alt="MathsCampus Logo" className="h-14 w-auto" />
            <span className="ml-3 text-3xl font-playfair font-bold text-charcoal" style={{ letterSpacing: '-0.02em' }}>MathsCampus</span>
          </div>
          <h2 className="text-3xl font-playfair font-bold text-charcoal mb-3" style={{ letterSpacing: '-0.02em' }}>Welcome Back</h2>
          <p className="text-gray-600 text-base">Sign in to continue your learning journey</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="your@email.com"
              style={{ fontSize: '15px' }}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-primary focus:border-primary transition-all"
              placeholder="••••••••"
              style={{ fontSize: '15px' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all transform ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-dark hover:scale-[1.02] shadow-lg hover:shadow-xl'
            }`}
            style={{ fontSize: '16px', marginTop: '2rem' }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-8 text-center space-y-4">
          <button
            onClick={onSwitchToForgotPassword}
            className="text-primary hover:text-primary font-medium text-sm transition-colors"
            style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
          >
            Forgot your password?
          </button>

          <div className="text-gray-600 text-base">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToSignup}
              className="text-primary hover:text-primary font-bold transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 transition-colors text-2xl font-light"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default LoginPage;